import React, { useMemo, useState } from "react";
import api, { API, getToken } from "@/lib/api";
import { toast } from "sonner";
import { Download, Package, FileText, Search, Flame, Zap, Combine } from "lucide-react";

const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

const commodityBadge = (c) => {
  if (c === "gas") return { label: "Gas", classes: "bg-orange-100 text-orange-800 border-orange-200", Icon: Flame };
  if (c === "power") return { label: "Power", classes: "bg-blue-100 text-blue-800 border-blue-200", Icon: Zap };
  if (c === "both") return { label: "Dual", classes: "bg-purple-100 text-purple-800 border-purple-200", Icon: Combine };
  return { label: "—", classes: "bg-slate-100 text-slate-600 border-slate-200", Icon: FileText };
};

/**
 * Archivio Controllo Fatture (admin + cliente).
 *
 * Props:
 *  - reports: array dei custom_reports (il parent passa SOLO quelli con is_invoice_check=true)
 *  - scope: "admin" | "client" — controlla alcune colonne (admin vede l'azienda)
 *  - companyIdForZip: id azienda da passare al bulk-download (admin). Se null, lato server
 *                     il cliente usa la propria company automaticamente.
 *  - companyName: label dell'azienda mostrata in header admin (opzionale)
 */
export default function InvoiceCheckArchive({ reports, scope = "client", companyIdForZip = null, companyName = null, pdpLabels = {}, pdpAddresses = {} }) {
  const [filterCommodity, setFilterCommodity] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [searchText, setSearchText] = useState("");
  const [downloadingZip, setDownloadingZip] = useState(false);

  // Anni disponibili calcolati dai dati
  const availableYears = useMemo(() => {
    const s = new Set();
    for (const r of reports) if (r.billing_year) s.add(r.billing_year);
    return Array.from(s).sort((a, b) => b - a);
  }, [reports]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return reports
      .filter((r) => !filterCommodity || r.commodity === filterCommodity)
      .filter((r) => !filterYear || r.billing_year === Number(filterYear))
      .filter((r) => !filterMonth || r.billing_month === Number(filterMonth))
      .filter((r) => {
        if (!q) return true;
        return (
          (r.title || "").toLowerCase().includes(q) ||
          (r.pod || "").toLowerCase().includes(q) ||
          (r.filename || "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        // Ordina per anno DESC, mese DESC, poi created_at DESC
        if (a.billing_year !== b.billing_year) return (b.billing_year || 0) - (a.billing_year || 0);
        if (a.billing_month !== b.billing_month) return (b.billing_month || 0) - (a.billing_month || 0);
        return (b.created_at || "").localeCompare(a.created_at || "");
      });
  }, [reports, filterCommodity, filterYear, filterMonth, searchText]);

  const downloadZip = async () => {
    if (!filterYear || !filterMonth) {
      toast.error("Seleziona mese e anno per scaricare lo ZIP");
      return;
    }
    setDownloadingZip(true);
    try {
      const params = new URLSearchParams();
      params.set("year", String(filterYear));
      params.set("month", String(filterMonth));
      if (filterCommodity) params.set("commodity", filterCommodity);
      if (companyIdForZip) params.set("company_id", companyIdForZip);
      const url = `${API}/custom-reports/invoice-check/bulk-download?${params.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const monthLabel = MONTHS_IT[filterMonth - 1] || filterMonth;
      const commLabel = filterCommodity ? `_${filterCommodity.toUpperCase()}` : "";
      link.download = `ControlloFatture_${monthLabel}${filterYear}${commLabel}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`ZIP scaricato (${filtered.length} file)`);
    } catch (e) {
      toast.error(e.message || "Errore download ZIP");
    } finally {
      setDownloadingZip(false);
    }
  };

  if (!reports.length) {
    return (
      <div className="card-elevated p-6 text-center" data-testid="invoice-archive-empty">
        <FileText size={32} className="mx-auto text-slate-300" />
        <div className="mt-2 text-sm text-slate-500">Nessun controllo fatture presente.</div>
      </div>
    );
  }

  return (
    <div className="card-elevated p-5" data-testid="invoice-archive">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <Package size={16} className="text-sky-600" />
            Archivio Controllo Fatture
            {companyName && <span className="text-sm font-normal text-slate-500">· {companyName}</span>}
          </h3>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {filtered.length} di {reports.length} documenti
          </div>
        </div>
        <button
          disabled={downloadingZip || !filterYear || !filterMonth || filtered.length === 0}
          onClick={downloadZip}
          className="text-sm px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-semibold flex items-center gap-1.5 disabled:bg-slate-300 disabled:cursor-not-allowed hover:bg-emerald-700"
          data-testid="invoice-archive-zip-btn"
          title={!filterYear || !filterMonth ? "Seleziona mese e anno per scaricare lo ZIP" : "Scarica tutti i PDF del mese in un ZIP"}
        >
          <Download size={14} /> Scarica ZIP mese
        </button>
      </div>

      {/* Filtri */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-4">
        <select
          value={filterCommodity}
          onChange={(e) => setFilterCommodity(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
          data-testid="invoice-archive-filter-commodity"
        >
          <option value="">Tutti i tipi</option>
          <option value="gas">Gas</option>
          <option value="power">Power</option>
          <option value="both">Dual</option>
        </select>
        <select
          value={filterYear}
          onChange={(e) => setFilterYear(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
          data-testid="invoice-archive-filter-year"
        >
          <option value="">Tutti gli anni</option>
          {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
          data-testid="invoice-archive-filter-month"
        >
          <option value="">Tutti i mesi</option>
          {MONTHS_IT.map((lbl, i) => <option key={i} value={i + 1}>{lbl}</option>)}
        </select>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Titolo o POD/PDR…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
            data-testid="invoice-archive-search"
          />
        </div>
      </div>

      {/* Tabella */}
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Mese/Anno</th>
              <th className="text-left px-3 py-2 font-semibold">Tipo</th>
              <th className="text-left px-3 py-2 font-semibold">Titolo</th>
              <th className="text-left px-3 py-2 font-semibold">PDP (POD/PDR)</th>
              <th className="text-right px-3 py-2 font-semibold w-20">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const badge = commodityBadge(r.commodity);
              const monthLabel = r.billing_month ? MONTHS_IT[r.billing_month - 1] : "—";
              const dlUrl = `${API}/custom-reports/${r.id}/download?auth=${encodeURIComponent(getToken() || "")}`;
              return (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`invoice-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-800 font-medium">{monthLabel} {r.billing_year || ""}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full border ${badge.classes}`}>
                      <badge.Icon size={10} /> {badge.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-700 truncate" style={{maxWidth: 320}} title={r.title}>{r.title}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-600">
                    {r.pod ? (
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1 flex-wrap">
                          <span>{r.pod}</span>
                          {pdpLabels[(r.pod || "").toUpperCase()] && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-sans font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-1.5 py-0.5">
                              {pdpLabels[(r.pod || "").toUpperCase()]}
                            </span>
                          )}
                        </div>
                        {(pdpAddresses[(r.pod || "").toUpperCase()] || r.address) && (
                          <span className="font-sans text-[10px] text-slate-400 truncate" style={{maxWidth: 260}} title={pdpAddresses[(r.pod || "").toUpperCase()] || r.address}>
                            {pdpAddresses[(r.pod || "").toUpperCase()] || r.address}
                          </span>
                        )}
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <a
                      href={dlUrl}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-100"
                      data-testid={`invoice-dl-${r.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Download size={12} /> PDF
                    </a>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-sm text-slate-400">
                  Nessun risultato con i filtri selezionati
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
